(() => {
  const DEFAULT_ERROR = 'Die Konfiguration konnte nicht vollständig in den Warenkorb gelegt werden. Bitte versuchen Sie es erneut.';

  const clone = (value) => JSON.parse(JSON.stringify(value));
  const integer = (value, fallback = 0) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  function defaultState(config) {
    return { ...clone(config.defaults), quantityOverrides: {} };
  }

  function resolveConfiguration(config, liveVariants, candidateState) {
    const state = { ...defaultState(config), ...clone(candidateState || {}) };
    state.extraLengths = { ...config.defaults.extraLengths, ...(state.extraLengths || {}) };
    state.extraHolder = { ...config.defaults.extraHolder, ...(state.extraHolder || {}) };
    state.quantityOverrides = { ...(state.quantityOverrides || {}) };

    const recipe = config.heightRecipes.find(({ meters }) => Number(meters) === Number(state.height));
    const errors = [];
    const included = config.baseKit.components.map(({ componentKey, quantity }) => ({ componentKey, quantity, mode: 'included' }));
    const lines = new Map();
    const replacements = [];

    const add = (componentKey, quantity, mode, source, editable = true) => {
      const count = Math.max(0, integer(quantity));
      if (!componentKey || count === 0) return;
      const current = lines.get(componentKey) || { componentKey, minimum: 0, mode, sources: [], sourceQuantities: {}, editable };
      current.minimum += count;
      current.sources.push(source);
      current.sourceQuantities[source] = (current.sourceQuantities[source] || 0) + count;
      current.editable ||= editable;
      lines.set(componentKey, current);
    };
    const replace = (from, to, source) => {
      if (!to || from === to) return;
      replacements.push({ from, to, source });
      add(to, 1, 'replace', source, false);
    };

    if (!recipe) errors.push('Für diese Höhe ist kein technisches Rezept hinterlegt.');
    for (const item of recipe?.additions || []) add(item.componentKey, item.quantity, 'recipe', 'height', true);
    add(state.wallholder, recipe?.additionalWallholders || 0, 'recipe', 'height-wallholders', true);

    replace('console50', state.console, 'console');
    add(state.roof, 1, 'add', 'roof', false);
    add('cleaning', Math.max(0, integer(state.cleaning, 1) - 1), 'add', 'cleaning', true);
    add('fireConnection', Math.max(0, integer(state.fireConnections, 1) - 1), 'add', 'fire', true);
    replace('termination', state.termination, 'termination');
    replace('transition250', state.transition, 'transition');
    add(state.bend, state.bend ? 1 : 0, 'add', 'bend', true);
    for (const key of ['length360', 'length540', 'length1080']) add(key, state.extraLengths[key], 'add', 'extra-length', true);
    add(state.extraHolder.componentKey, state.extraHolder.quantity, 'add', 'extra-holder', true);

    const chargeable = [...lines.values()].map((line) => {
      const requested = integer(state.quantityOverrides[line.componentKey], line.minimum);
      const quantity = Math.max(line.minimum, requested);
      const variantId = config.componentVariantMap[line.componentKey];
      const variant = liveVariants[String(variantId)];
      if (!variant) errors.push(`Shopify-Variante für ${line.componentKey} fehlt.`);
      else if (!variant.available) errors.push(`${variant.productTitle || variant.title} ist derzeit nicht verfügbar.`);
      return { ...line, quantity, variantId: String(variantId || ''), variant };
    });

    const base = liveVariants[String(config.baseVariantId)];
    if (!base) errors.push('Die Shopify-Basisvariante fehlt.');
    else if (!base.available) errors.push('Der Schornstein-Bausatz ist derzeit nicht verfügbar.');
    const componentTotal = chargeable.reduce((sum, line) => sum + (line.variant?.price || 0) * line.quantity, 0);

    return {
      state,
      recipe,
      included,
      chargeable,
      replacements,
      errors: [...new Set(errors)],
      base,
      basePrice: base?.price || 0,
      componentTotal,
      total: (base?.price || 0) + componentTotal,
    };
  }

  function configurationId() {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
    return `chimney-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function buildCartPayload(config, resolved, id = configurationId()) {
    if (resolved.errors.length) throw new Error(resolved.errors.join(' '));
    const common = {
      _chimney_configuration_id: id,
      _chimney_ruleset: config.ruleset,
      _chimney_diameter: `Ø${config.diameter}`,
    };
    const properties = {
      ...common,
      _chimney_height: `${String(resolved.state.height).replace('.', ',')} m`,
      _chimney_replacements: resolved.replacements.map(({ from, to }) => `${from}>${to}`).join(', '),
    };
    return {
      id,
      items: [
        { id: Number(config.baseVariantId), quantity: 1, properties },
        ...resolved.chargeable.map((line) => ({
          id: Number(line.variantId),
          quantity: line.quantity,
          properties: { ...common, _chimney_component: line.componentKey, _chimney_mode: line.mode },
        })),
      ],
    };
  }

  function validateCartResponse(payload, response) {
    const returned = Array.isArray(response?.items) ? response.items : [];
    if (returned.length !== payload.items.length) return false;
    const expected = new Map(payload.items.map(({ id, quantity }) => [String(id), quantity]));
    const actual = new Map();
    for (const item of returned) {
      const id = String(item.variant_id || item.id || '');
      actual.set(id, (actual.get(id) || 0) + integer(item.quantity));
    }
    return [...expected].every(([id, quantity]) => actual.get(id) === quantity);
  }

  async function addConfigurationToCart(fetchImplementation, url, payload) {
    const response = await fetchImplementation(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: payload.items }),
    });
    let body = null;
    try { body = await response.json(); } catch (_) { /* handled below */ }
    if (!response.ok || !validateCartResponse(payload, body)) throw new Error(body?.description || DEFAULT_ERROR);
    return body;
  }

  const option = (value, label, selected = false, disabled = false) =>
    `<option value="${value ?? ''}"${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}>${label}</option>`;

  const HTMLElementBase = globalThis.HTMLElement || class {};

  class ChimneyConfigurator extends HTMLElementBase {
    connectedCallback() {
      if (this.initialized) return;
      this.config = JSON.parse(this.querySelector('[data-chimney-config]')?.textContent || '{}');
      this.liveVariants = JSON.parse(this.querySelector('[data-chimney-variants]')?.textContent || '{}');
      if (this.config.ruleset !== 'eka-complex-d-v1') return;
      this.state = defaultState(this.config);
      this.step = 0;
      this.partsOpen = false;
      this.submitting = false;
      this.initialized = true;
      this.render();
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('toggle', (event) => {
        if (event.target.matches?.('.chimney-configurator__parts')) this.partsOpen = event.target.open;
      }, true);
      this.handleDocumentClick = (event) => {
        if (event.target.closest?.('.chimney-configurator__info')) return;
        this.closeInfoTooltips();
      };
      this.handleDocumentKeydown = (event) => {
        if (event.key === 'Escape') this.closeInfoTooltips();
      };
      this.handleViewportChange = () => this.closeInfoTooltips();
      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('scroll', this.handleViewportChange, true);
      window.addEventListener('resize', this.handleViewportChange);
    }

    disconnectedCallback() {
      if (this.handleDocumentClick) document.removeEventListener('click', this.handleDocumentClick);
      if (this.handleDocumentKeydown) document.removeEventListener('keydown', this.handleDocumentKeydown);
      if (this.handleViewportChange) {
        document.removeEventListener('scroll', this.handleViewportChange, true);
        window.removeEventListener('resize', this.handleViewportChange);
      }
    }

    money(cents) {
      return new Intl.NumberFormat(this.dataset.locale || 'de-DE', {
        style: 'currency', currency: this.dataset.currency || 'EUR',
      }).format(cents / 100);
    }

    component(key) {
      return this.liveVariants[String(this.config.componentVariantMap[key])];
    }

    isUnavailable(key) {
      const variant = this.component(key);
      return key && (!variant || !variant.available);
    }

    info(text) {
      return `<span class="chimney-configurator__info"><button type="button" aria-label="Erklärung anzeigen" aria-expanded="false" data-info-toggle>ⓘ</button><span role="tooltip">${text}</span></span>`;
    }

    closeInfoTooltips() {
      this.querySelectorAll('[data-info-toggle][aria-expanded="true"]').forEach((button) => {
        const mobile = window.matchMedia('(max-width: 749px)').matches;
        button.setAttribute('aria-expanded', 'false');
        const wrapper = button.closest('.chimney-configurator__info');
        const tooltip = button.nextElementSibling;
        wrapper?.classList.remove('is-open');
        if (tooltip && mobile) {
          tooltip.style.visibility = 'hidden';
          tooltip.style.opacity = '0';
          tooltip.style.removeProperty('width');
          tooltip.style.removeProperty('top');
          tooltip.style.removeProperty('left');
        } else if (tooltip) {
          tooltip.removeAttribute('style');
        }
      });
    }

    positionInfoTooltip(button) {
      const tooltip = button.nextElementSibling;
      const wrapper = button.closest('.chimney-configurator__info');
      if (!tooltip || !wrapper || !window.matchMedia('(max-width: 749px)').matches) return null;
      const viewportGap = 8;
      const width = Math.min(288, window.innerWidth - viewportGap * 2);
      tooltip.style.visibility = 'hidden';
      tooltip.style.opacity = '0';
      tooltip.style.width = `${width}px`;
      tooltip.style.top = '0px';
      tooltip.style.left = '0px';
      const triggerRect = button.getBoundingClientRect();
      const tooltipHeight = tooltip.getBoundingClientRect().height;
      const left = Math.min(window.innerWidth - width - viewportGap, Math.max(viewportGap, triggerRect.left + triggerRect.width / 2 - width / 2));
      const above = triggerRect.top - tooltipHeight - viewportGap;
      const top = above >= viewportGap
        ? above
        : Math.min(window.innerHeight - tooltipHeight - viewportGap, triggerRect.bottom + viewportGap);
      tooltip.style.left = `${Math.round(left)}px`;
      tooltip.style.top = `${Math.max(viewportGap, Math.round(top))}px`;
      wrapper.classList.add('is-open');
      return tooltip;
    }

    select(name, entries, value) {
      return `<select name="${name}" data-state="${name}">${entries.map(({ key, label, componentKey = key }) => option(key, `${label}${this.isUnavailable(componentKey) ? ' – nicht verfügbar' : ''}`, key === value, this.isUnavailable(componentKey))).join('')}</select>`;
    }

    choiceButtons(name, entries, value) {
      return `<div class="chimney-configurator__choices">${entries.map(({ key, label, hint, componentKey }) => {
        const unavailable = componentKey && this.isUnavailable(componentKey);
        return `<label class="chimney-configurator__choice${String(value) === String(key) ? ' is-selected' : ''}${unavailable ? ' is-unavailable' : ''}">
          <input type="radio" name="${name}" data-state="${name}" value="${key}"${String(value) === String(key) ? ' checked' : ''}${unavailable ? ' disabled' : ''}>
          <strong>${label}</strong>${hint ? `<small>${hint}</small>` : ''}
        </label>`;
      }).join('')}</div>`;
    }

    displayName(componentKey, fallback = '') {
      const names = {
        roofFlat: 'Dachdurchführung flach 0°', roof22: 'Dachdurchführung 22°', roof32: 'Dachdurchführung 32°', roof45: 'Dachdurchführung 45°',
        cleaning: 'Reinigungsöffnung', fireConnection: 'Feuerungsanschluss 90°',
        bend15: 'Bogen 15°', bend30: 'Bogen 30°', bend45: 'Bogen 45°', bend90: 'Bogen 90°',
        rainHood: 'Regenhaube mit Windabweiser', termination: 'Standardabschluss',
        length360: 'Längenelement 360 mm', length540: 'Längenelement 540 mm', length1080: 'Längenelement 1080 mm',
        console50: 'Wandkonsole 50 mm', console50_90: 'Wandkonsole 50–90 mm', console50_250: 'Wandkonsole 50–250 mm', console50_500: 'Wandkonsole 50–500 mm',
        holder75: 'Wandhalter 75 mm', holder50: 'Wandhalter 50 mm', holder50_90: 'Wandhalter 50–90 mm', holder90_160: 'Wandhalter 90–160 mm', holder160_250: 'Wandhalter 160–250 mm', holder250_400: 'Wandhalter 250–400 mm',
        basePlate: 'Bodenplatte mit Kondensatablauf', transition250: 'Verbindung zum Ofenrohr 250 mm', transition500: 'Verbindung zum Ofenrohr 500 mm', transition1000: 'Verbindung zum Ofenrohr 1000 mm', transitionDwEw: 'Übergang DW/EW mit Wärmedämmung',
      };
      return names[componentKey] || fallback || componentKey;
    }

    summaryChoice(label, value, step) {
      return `<span><small>${label}</small><strong>${value}</strong><button type="button" data-edit-step="${step}" aria-label="${label} ändern">Ändern</button></span>`;
    }

    preview(resolved, roofLabel) {
      const height = Number(this.state.height || 3);
      const consoleReach = { console50: 30, console50_90: 37, console50_250: 50, console50_500: 68 }[this.state.console] || 30;
      const holderReach = { holder50: 28, holder75: 32, holder50_90: 37, holder90_160: 45, holder160_250: 54, holder250_400: 64 }[this.state.wallholder] || 32;
      const wallX = 112;
      const pipeWidth = 36;
      const pipeX = wallX + Math.max(consoleReach, holderReach);
      const pipeCenter = pipeX + pipeWidth / 2;
      const pipeBottom = 430;
      const supportStrokeWidth = 8;
      const supportCenterY = pipeBottom - supportStrokeWidth / 2;
      const pipeTop = Math.max(58, 228 - ((height - 3) / 9) * 145);
      const angle = { bend15: 15, bend30: 30, bend45: 45, bend90: 90 }[this.state.bend] || 0;
      const bendY = pipeTop + (angle === 90 ? 92 : 78);
      const angledLength = angle === 90 ? 92 : 82;
      const topX = angle ? pipeCenter + Math.sin(angle * Math.PI / 180) * angledLength : pipeCenter;
      const topY = angle ? bendY - Math.cos(angle * Math.PI / 180) * angledLength : pipeTop;
      const pipePath = angle ? `M ${pipeCenter} ${pipeBottom} V ${bendY} L ${topX} ${topY}` : `M ${pipeCenter} ${pipeBottom} V ${pipeTop}`;
      const segmentCount = Math.max(3, Math.min(10, Math.round(height / 1.25)));
      const collarBottom = angle ? bendY + 8 : pipeTop;
      const collars = Array.from({ length: segmentCount }, (_, index) => {
        const y = pipeBottom - ((pipeBottom - collarBottom) / segmentCount) * (index + 1);
        return `<line x1="${pipeX - 2}" y1="${y}" x2="${pipeX + pipeWidth + 2}" y2="${y}" stroke="#737778" stroke-width="2"/>`;
      }).join('');
      const automaticHolders = 1 + integer(resolved.recipe?.additionalWallholders);
      const voluntaryHolders = integer(this.state.extraHolder?.quantity);
      const holderCount = Math.min(5, automaticHolders + voluntaryHolders);
      const holderSpacing = Math.min(60, 190 / Math.max(1, holderCount - 1));
      const topmostHolderY = 390 - (holderCount - 1) * holderSpacing;
      const wallBottom = 466;
      const wallTop = Math.min(318, topmostHolderY - 18);
      const wallHeight = wallBottom - wallTop;
      const holders = Array.from({ length: holderCount }, (_, index) => {
        const y = 390 - index * holderSpacing;
        const voluntary = index >= automaticHolders;
        const reach = voluntary ? ({ holder50: 28, holder75: 32, holder50_90: 37, holder90_160: 45, holder160_250: 54, holder250_400: 64 }[this.state.extraHolder?.componentKey] || holderReach) : holderReach;
        return `<g aria-hidden="true"><line x1="${wallX}" y1="${y}" x2="${pipeX}" y2="${y}" stroke="${voluntary ? '#bd1734' : '#737373'}" stroke-width="5"${voluntary ? ' stroke-dasharray="6 3"' : ''}/><rect x="${wallX - 7}" y="${y - 8}" width="9" height="16" class="chimney-configurator__preview-holder-bracket" fill="#bd1734"/><line x1="${wallX}" y1="${y + 5}" x2="${wallX + Math.min(reach, pipeX - wallX)}" y2="${y + 5}" stroke="#b7b7b3" stroke-width="2"/></g>`;
      }).join('');
      const roofDrop = { roofFlat: 0, roof22: 34, roof32: 54, roof45: 82 }[this.state.roof] || 0;
      const roofY = 316;
      const roofAngle = -Math.atan2(roofDrop, Math.max(1, pipeX - 28)) * 180 / Math.PI;
      const roof = this.state.roof ? `<g aria-hidden="true"><path d="M 28 ${roofY + roofDrop} L ${pipeX + pipeWidth + 10} ${roofY}" fill="none" stroke="#242424" stroke-width="10"/><path d="M 28 ${roofY + roofDrop + 7} L ${pipeX + pipeWidth + 10} ${roofY + 7}" fill="none" stroke="#d7d7d3" stroke-width="3"/><rect x="${pipeX - 25}" y="${roofY - 8}" width="${pipeWidth + 50}" height="16" fill="#bd1734" stroke="#8f1027" transform="rotate(${roofAngle} ${pipeCenter} ${roofY})"/></g>` : '';
      const termination = this.state.termination === 'rainHood'
        ? `<g transform="translate(${topX} ${topY}) rotate(${angle})" aria-hidden="true"><line y1="0" y2="-25" stroke="#777" stroke-width="4"/><path d="M -31 -25 H 31 L 19 -40 H -19 Z" fill="#bd1734" stroke="#7d1023"/></g>`
        : `<g transform="translate(${topX} ${topY}) rotate(${angle})" aria-hidden="true"><line y1="0" y2="-9" stroke="#777" stroke-width="4"/><rect x="-23" y="-18" width="46" height="10" fill="#aeb2b2" stroke="#737778"/><path d="M -19 -18 L -12 -30 H 12 L 19 -18 Z" fill="#d8d9d8" stroke="#737778"/></g>`;
      const transitionLength = { transition250: 43, transition500: 55, transition1000: 72, transitionDwEw: 58 }[this.state.transition] || 43;
      const connections = Array.from({ length: integer(this.state.fireConnections, 1) }, (_, index) => {
        const y = 405 - index * 54;
        return `<g aria-hidden="true"><rect x="${pipeX + pipeWidth - 2}" y="${y - 11}" width="${transitionLength}" height="22" fill="#bfc2c2" stroke="#737778"/><ellipse cx="${pipeX + pipeWidth - 2 + transitionLength}" cy="${y}" rx="6" ry="11" fill="#969a9a"/>${this.state.transition === 'transitionDwEw' ? `<rect x="${pipeX + pipeWidth + 9}" y="${y - 15}" width="17" height="30" fill="none" stroke="#bd1734" stroke-width="3"/>` : ''}</g>`;
      }).join('');
      const cleaningOpenings = Array.from({ length: integer(this.state.cleaning, 1) }, (_, index) => {
        const y = 438 - index * 58;
        return `<g aria-hidden="true"><rect x="${pipeX + 5}" y="${y - 19}" width="26" height="34" rx="2" fill="#aeb2b2" stroke="#5f6363" stroke-width="2"/><circle cx="${pipeX + 26}" cy="${y - 2}" r="2.5" fill="#bd1734"/></g>`;
      }).join('');
      const dimensionTop = Math.min(pipeTop, topY);
      const statusItems = this.previewStatus(resolved, roofLabel);

      return `<div class="chimney-configurator__preview-stage">
        <p class="chimney-configurator__preview-label">Vorschau</p>
        <svg viewBox="0 0 360 520" role="img" aria-label="Schematische Vorschau eines ${String(height).replace('.', ',')} Meter hohen Edelstahlschornsteins">
          <rect x="28" y="${wallTop}" width="${wallX - 28}" height="${wallHeight}" class="chimney-configurator__preview-wall" fill="#efefec"/>
          <rect x="${wallX - 8}" y="${wallTop}" width="8" height="${wallHeight}" class="chimney-configurator__preview-wall-edge" fill="#deded9"/>
          ${roof}
          ${holders}
          <path d="M ${wallX} ${supportCenterY} H ${pipeX}" class="chimney-configurator__preview-support" stroke="#6d6d6d" stroke-width="${supportStrokeWidth}"/>
          <rect x="${wallX - 2}" y="418" width="18" height="25" fill="#bd1734"/>
          <path d="${pipePath}" fill="none" stroke="#707475" stroke-width="40" stroke-linecap="butt" stroke-linejoin="round"/>
          <path d="${pipePath}" fill="none" stroke="#d8d9d8" stroke-width="36" stroke-linecap="butt" stroke-linejoin="round"/>
          <path d="${pipePath}" fill="none" stroke="#f7f7f5" stroke-width="4" opacity=".9" transform="translate(-8 0)"/>
          ${collars}
          ${termination}
          ${connections}
          ${cleaningOpenings}
          <line x1="332" y1="${dimensionTop}" x2="332" y2="${pipeBottom}" stroke="#bd1734" stroke-width="2"/>
          <path d="M 327 ${dimensionTop + 8} L 332 ${dimensionTop} L 337 ${dimensionTop + 8} M 327 ${pipeBottom - 8} L 332 ${pipeBottom} L 337 ${pipeBottom - 8}" fill="none" stroke="#bd1734" stroke-width="2"/>
          <text x="324" y="${(dimensionTop + pipeBottom) / 2}" fill="#bd1734" font-size="13" text-anchor="end" transform="rotate(-90 324 ${(dimensionTop + pipeBottom) / 2})">${String(height).replace('.', ',')} m</text>
        </svg>
      </div>
      <div class="chimney-configurator__preview-meta">${statusItems.map(([label, value]) => `<span><small>${label}</small><strong>${value}</strong></span>`).join('')}</div>`;
    }

    previewStatus(resolved, roofLabel) {
      const height = `${String(this.state.height).replace('.', ',')} m`;
      const automatic = integer(resolved.recipe?.additionalWallholders);
      const consoleLabels = { console50: '5 cm', console50_90: '5–9 cm', console50_250: '5–25 cm', console50_500: '5–50 cm' };
      const holderLabels = { holder50: '5 cm', holder75: '7,5 cm', holder50_90: '5–9 cm', holder90_160: '9–16 cm', holder160_250: '16–25 cm', holder250_400: '25–40 cm' };
      const transitionLabels = { transition250: '250 mm', transition500: '500 mm', transition1000: '1000 mm', transitionDwEw: 'DW/EW gedämmt' };
      const extraLengths = Object.values(this.state.extraLengths || {}).reduce((sum, value) => sum + integer(value), 0);
      if (this.step === 0) return [['Höhe', height], ['Längenelemente', 'automatisch berechnet'], ['Wandhalter', automatic ? `${automatic} automatisch` : 'Basissatz ausreichend']];
      if (this.step === 1) return [['Höhe', height], ['Wandkonsole', consoleLabels[this.state.console]], ['Wandhalter', holderLabels[this.state.wallholder]]];
      if (this.step === 2) return [['Höhe', height], ['Dach', roofLabel], ['Wandhalter', `${automatic} automatisch`]];
      if (this.step === 3) return [['Öfen', String(this.state.fireConnections)], ['Reinigung', `${this.state.cleaning} Öffnung${this.state.cleaning === 1 ? '' : 'en'}`], ['Verbindung', transitionLabels[this.state.transition]]];
      if (this.step === 4) return [['Abschluss', this.state.termination === 'rainHood' ? 'Regenhaube' : 'Standard'], ['Bogen', this.state.bend ? this.displayName(this.state.bend) : 'Keiner'], ['Zusatzteile', `${extraLengths + integer(this.state.extraHolder?.quantity)} Stück`]];
      return [['Höhe', height], ['Dach', roofLabel], ['Wandhalterabstand', holderLabels[this.state.wallholder]]];
    }

    render() {
      const resolved = resolveConfiguration(this.config, this.liveVariants, this.state);
      this.resolved = resolved;
      const diameterLabel = `Ø ${this.config.diameter} mm`;
      const heights = this.config.heightRecipes.map(({ meters }) => ({ key: String(meters), label: `${String(meters).replace('.', ',')} m`, componentKey: null }));
      const holderEntries = [
        ['holder75', '75 mm / 7,5 cm (Standard)'], ['holder50', '50 mm / 5 cm'], ['holder50_90', 'verstellbar 50–90 mm / 5–9 cm'],
        ['holder90_160', 'verstellbar 90–160 mm / 9–16 cm'], ['holder160_250', 'verstellbar 160–250 mm / 16–25 cm'], ['holder250_400', 'verstellbar 250–400 mm / 25–40 cm'],
      ].map(([key, label]) => ({ key, label }));
      const consoleEntries = [
        ['console50', '50 mm / 5 cm (im Basissatz enthalten)'], ['console50_90', 'verstellbar 50–90 mm / 5–9 cm'],
        ['console50_250', 'verstellbar 50–250 mm / 5–25 cm'], ['console50_500', 'verstellbar 50–500 mm / 5–50 cm'],
      ].map(([key, label]) => ({ key, label }));
      const transitionEntries = [
        ['transition250', '250 mm, kürzbar (Standard)'], ['transition500', '500 mm, kürzbar'],
        ['transition1000', '1000 mm, kürzbar'], ['transitionDwEw', 'Übergang doppelwandig / einwandig mit Wärmedämmung (DW/EW)'],
      ].map(([key, label]) => ({ key, label }));
      const roofEntries = [[null, 'Keine'], ['roofFlat', 'Flach 0°'], ['roof22', '22°'], ['roof32', '32°'], ['roof45', '45°']];
      const steps = ['Höhe', 'Befestigung', 'Dachdurchführung', 'Anschluss & Reinigung', 'Extras', 'Zusammenfassung'];
      const progress = Math.round(((this.step + 1) / steps.length) * 100);
      const roofLabel = roofEntries.find(([key]) => String(key || '') === String(this.state.roof || ''))?.[1] || 'Keine';
      const holderLabel = holderEntries.find(({ key }) => key === this.state.wallholder)?.label || 'Standard';
      const consoleLabel = consoleEntries.find(({ key }) => key === this.state.console)?.label || 'Standard';
      const transitionLabel = transitionEntries.find(({ key }) => key === this.state.transition)?.label || 'Standard';
      const connectionLabel = this.state.fireConnections === 1 ? '1 Ofen' : '2 Öfen';
      const extraLengthLabel = Object.entries(this.state.extraLengths).filter(([, quantity]) => integer(quantity)).map(([key, quantity]) => `${quantity} × ${key.replace('length', '')} mm`).join(', ') || 'Keine';
      const extraHolderLabel = integer(this.state.extraHolder.quantity) ? `${this.state.extraHolder.quantity} × ${this.displayName(this.state.extraHolder.componentKey)}` : 'Keine';
      const bendLabel = this.state.bend ? this.displayName(this.state.bend) : 'Kein Versatz';
      const terminationLabel = this.state.termination === 'rainHood' ? 'Regenhaube mit Windabweiser' : 'Standardabschluss';

      this.querySelector('[data-chimney-app]').innerHTML = `
        <div class="chimney-configurator__head">
          <div class="chimney-configurator__head-system">
            <h2>Edelstahlschornstein konfigurieren</h2>
            <span class="chimney-configurator__diameter">${diameterLabel}</span>
          </div>
          <div class="chimney-configurator__head-price">
            <strong class="chimney-configurator__live-price" role="status" aria-live="polite">${this.money(resolved.total)}</strong>
            <small>Aktuelle Konfiguration</small>
          </div>
        </div>
        <div class="chimney-configurator__progress" aria-label="Schritt ${this.step + 1} von ${steps.length}">
          <div class="chimney-configurator__progress-row">
            <strong class="chimney-configurator__progress-name">${steps[this.step]}</strong>
            <span class="chimney-configurator__progress-count">Schritt ${this.step + 1} von ${steps.length}</span>
          </div>
          <i class="chimney-configurator__progress-track" style="--progress:${progress}%"></i>
        </div>
        <div class="chimney-configurator__workspace">
          <aside class="chimney-configurator__preview" aria-live="polite">${this.preview(resolved, roofLabel)}</aside>
          <form class="chimney-configurator__panel" data-wizard-form novalidate>
            <section class="chimney-configurator__step" data-step="0"${this.step !== 0 ? ' hidden' : ''}>
              <div class="chimney-configurator__step-title h3" tabindex="-1">Wie hoch soll Ihr Schornstein werden?</div>
              <p class="chimney-configurator__step-intro">Wählen Sie die benötigte Gesamthöhe. Die passenden Systemteile stellen wir im Hintergrund zusammen.</p>
              <label class="chimney-configurator__field"><span class="chimney-configurator__field-label">Gesamthöhe ${this.info('Gemessen wird die geplante Gesamthöhe des Schornsteins. Daraus berechnen wir passende Längenelemente und Befestigungen.')}</span>
                ${this.select('height', heights, String(this.state.height))}
              </label>
              <p class="chimney-configurator__notice">Für Ihre gewählte Höhe von ${String(this.state.height).replace('.', ',')} m berechnen wir die benötigten Längenelemente automatisch${resolved.recipe?.additionalWallholders ? ` und berücksichtigen mindestens ${resolved.recipe.additionalWallholders} zusätzliche Wandhalter` : ''}.</p>
            </section>
            <section class="chimney-configurator__step" data-step="1"${this.step !== 1 ? ' hidden' : ''}>
              <div class="chimney-configurator__step-title h3" tabindex="-1">Wie wird das System befestigt?</div>
              <p class="chimney-configurator__step-intro">Wählen Sie den benötigten Abstand des Schornsteins zur Fassade.</p>
              <p class="chimney-configurator__schematic-note">Schematische Darstellung: Maßgeblich sind die angegebenen Einstellbereiche. Bitte berücksichtigen Sie Ihren tatsächlichen Wandaufbau und lassen Sie die Einbausituation fachlich prüfen.</p>
              <label class="chimney-configurator__field"><span class="chimney-configurator__field-label">Abstand der Wandkonsole ${this.info('Die Wandkonsole ist der untere Träger des Systems. Wählen Sie ihren Einstellbereich passend zum geplanten Abstand zur Fassade.')}</span>
                ${this.select('console', consoleEntries, this.state.console)}
              </label>
              <label class="chimney-configurator__field"><span class="chimney-configurator__field-label">Abstand der Wandhalter ${this.info('Die Wandhalter befestigen den Schornstein oberhalb der Konsole. Wählen Sie ihren Einstellbereich passend zum geplanten Abstand zur Fassade.')}</span>
                ${this.select('wallholder', holderEntries, this.state.wallholder)}
              </label>
              <p class="chimney-configurator__notice">${resolved.recipe?.additionalWallholders ? `Für Ihre gewählte Höhe von ${String(this.state.height).replace('.', ',')} m berücksichtigen wir automatisch mindestens ${resolved.recipe.additionalWallholders} zusätzliche Wandhalter.` : `Für Ihre gewählte Höhe von ${String(this.state.height).replace('.', ',')} m ist der im Basissatz enthaltene Wandhalter ausreichend.`}</p>
            </section>
            <section class="chimney-configurator__step" data-step="2"${this.step !== 2 ? ' hidden' : ''}>
              <div class="chimney-configurator__step-title h3" tabindex="-1">Führt der Schornstein durch das Dach? ${this.info('Wählen Sie eine Dachdurchführung nur, wenn der Schornstein durch die Dachfläche geführt wird. Die angegebene Neigung dient der Auswahl des passenden Bauteils.')}</div>
              <p class="chimney-configurator__step-intro">Ohne Dachdurchführung wählen Sie „Keine“. Andernfalls genügt die ungefähre Dachneigung.</p>
              <div class="chimney-configurator__cards">${roofEntries.map(([key, label]) => {
              const variant = key ? this.component(key) : null;
              const unavailable = key && this.isUnavailable(key);
              return `<label class="chimney-configurator__card${String(this.state.roof || '') === String(key || '') ? ' is-selected' : ''}${unavailable ? ' is-unavailable' : ''}">
                <input type="radio" name="roof" data-state="roof" value="${key || ''}"${String(this.state.roof || '') === String(key || '') ? ' checked' : ''}${unavailable ? ' disabled' : ''}>
                ${variant?.image ? `<img src="${variant.image}" alt="" loading="lazy">` : '<span class="chimney-configurator__placeholder chimney-configurator__placeholder--none" aria-hidden="true"><i></i></span>'}
                <strong>${key ? label : 'Keine'}</strong><small>${unavailable ? 'Nicht verfügbar' : key ? `+ ${this.money(variant.price)}` : 'Keine Dachdurchführung'}</small>
              </label>`;
              }).join('')}</div>
            </section>
            <section class="chimney-configurator__step" data-step="3"${this.step !== 3 ? ' hidden' : ''}>
              <div class="chimney-configurator__step-title h3" tabindex="-1">Anschluss und Reinigung</div>
              <p class="chimney-configurator__step-intro">Legen Sie fest, wie viele Öfen angeschlossen werden und wie die Reinigung zugänglich bleibt.</p>
              <fieldset class="chimney-configurator__group chimney-configurator__group--first"><legend>Wie viele Öfen möchten Sie anschließen? ${this.info('Der Feuerungsanschluss verbindet das Ofenrohr mit dem Schornstein. Ein Anschluss ist im Basissatz enthalten.')}</legend>
                ${this.choiceButtons('fireConnections', [{ key: 1, label: '1 Ofen', hint: 'Standard · im Basissatz enthalten' }, { key: 2, label: '2 Öfen', hint: 'mit zweitem Feuerungsanschluss', componentKey: 'fireConnection' }], this.state.fireConnections)}
              </fieldset>
              <fieldset class="chimney-configurator__group"><legend>Reinigungsöffnungen ${this.info('Über diese verschließbare Öffnung wird der Schornstein kontrolliert und gereinigt. Eine Öffnung ist enthalten.')}</legend>
                ${this.choiceButtons('cleaning', [{ key: 1, label: '1 Öffnung', hint: 'Standard · im Basissatz enthalten' }, { key: 2, label: '2 Öffnungen', hint: 'mit zusätzlicher Reinigungsöffnung', componentKey: 'cleaning' }], this.state.cleaning)}
              </fieldset>
              <label class="chimney-configurator__field chimney-configurator__field--spaced"><span class="chimney-configurator__field-label">Verbindung zum Ofenrohr ${this.info('Das Übergangsstück verbindet das doppelwandige Schornsteinsystem (DW) mit dem einwandigen Ofenrohr (EW). Gemessen wird die benötigte Verbindungslänge bis zum Ofenrohr; 250, 500 und 1000 mm sind vor Ort kürzbar. DW/EW bezeichnet den gedämmten Übergang von doppelwandig zu einwandig.')}</span>
                ${this.select('transition', transitionEntries, this.state.transition)}
              </label>
            </section>
            <section class="chimney-configurator__step" data-step="4"${this.step !== 4 ? ' hidden' : ''}>
              <div class="chimney-configurator__step-title h3" tabindex="-1">Extras</div>
              <p class="chimney-configurator__step-intro">Ergänzen Sie nur, was Ihre Einbausituation wirklich benötigt. Die automatisch berechneten Teile bleiben erhalten.</p>
              <div class="chimney-configurator__section-title h4">Abschluss</div>
              <label class="chimney-configurator__field"><span class="chimney-configurator__field-label">Abschluss oben ${this.info('Die Regenhaube mit Windabweiser ersetzt den im Basissatz enthaltenen Standardabschluss.')}</span>
                ${this.select('termination', [{ key: 'termination', label: 'Standardabschluss' }, { key: 'rainHood', label: 'Regenhaube mit Windabweiser' }], this.state.termination)}
              </label>
              <div class="chimney-configurator__section-title h4">Seitlicher Versatz</div>
              <label class="chimney-configurator__field"><span class="chimney-configurator__field-label">Bogen ${this.info('Ein einzelner Bogen ändert die Richtung des Schornsteinverlaufs. Für einen vollständigen Versatz können weitere, separat geplante Bauteile erforderlich sein. Bitte lassen Sie die konkrete Führung fachlich prüfen.')}</span>
                ${this.select('bend', [{ key: '', label: 'Kein Bogen', componentKey: null }, ...[['bend15','Bogen 15°'],['bend30','Bogen 30°'],['bend45','Bogen 45°'],['bend90','Bogen 90°']].map(([key,label])=>({key,label}))], this.state.bend || '')}
              </label>
              <div class="chimney-configurator__section-title h4">Zusätzliche Bauteile</div>
              <fieldset class="chimney-configurator__group"><legend>Zusätzliche Längenelemente (optional) ${this.info('Diese Elemente bestellen Sie nur über die in Schritt 1 automatisch berechnete Gesamthöhe hinaus.')}</legend>
                <p class="chimney-configurator__group-help">Ihre gewählte Gesamthöhe von ${String(this.state.height).replace('.', ',')} m ist bereits automatisch zusammengestellt. Fügen Sie hier nur weitere Längenelemente hinzu, die über diese berechnete Anlagenhöhe hinaus separat benötigt werden.</p>
                ${['length360', 'length540', 'length1080'].map((key) => this.inlineQuantity(key, `${key.replace('length', '')} mm`, this.state.extraLengths[key], 0, 'extra-length')).join('')}
              </fieldset>
              <fieldset class="chimney-configurator__group"><legend>Weitere Wandhalter hinzufügen (optional) ${this.info('Diese Wandhalter kommen freiwillig zu den automatisch berechneten Haltern hinzu.')}</legend>
                <p class="chimney-configurator__automatic-count"><strong>Automatisch berücksichtigt:</strong> ${resolved.recipe?.additionalWallholders || 0} Wandhalter zusätzlich zum Basissatz</p>
                <label class="chimney-configurator__field">Ausführung des zusätzlichen Wandhalters ${this.select('extraHolder.componentKey', holderEntries, this.state.extraHolder.componentKey)}</label>
                ${this.inlineQuantity('extraHolder.quantity', 'Anzahl zusätzlicher Wandhalter', this.state.extraHolder.quantity, 0, 'extra-holder')}
              </fieldset>
            </section>
            <section class="chimney-configurator__step" data-step="5"${this.step !== 5 ? ' hidden' : ''}>
              <div class="chimney-configurator__step-title h3" tabindex="-1">Ihre Konfiguration</div>
              <p class="chimney-configurator__step-intro">Prüfen Sie die wichtigsten Angaben. Die technische Teileliste bleibt kompakt und kann bei Bedarf geöffnet werden.</p>
              <p class="chimney-configurator__system-name">EKA Complex D · ${diameterLabel}</p>
              <div class="chimney-configurator__compact-summary">
                ${this.summaryChoice('Höhe', `${String(this.state.height).replace('.', ',')} m`, 0)}
                ${this.summaryChoice('Wandkonsole', consoleLabel, 1)}
                ${this.summaryChoice('Wandhalter', holderLabel, 1)}
                ${this.summaryChoice('Dachdurchführung', roofLabel === 'Keine' ? 'Keine Dachdurchführung' : roofLabel, 2)}
                ${this.summaryChoice('Öfen', connectionLabel, 3)}
                ${this.summaryChoice('Reinigungsöffnungen', String(this.state.cleaning), 3)}
                ${this.summaryChoice('Verbindung zum Ofenrohr', transitionLabel, 3)}
                ${this.summaryChoice('Abschluss oben', terminationLabel, 4)}
                ${this.summaryChoice('Seitlicher Versatz', bendLabel, 4)}
                ${this.summaryChoice('Zusätzliche Längenelemente', extraLengthLabel, 4)}
                ${this.summaryChoice('Zusätzliche Wandhalter', extraHolderLabel, 4)}
              </div>
              <div class="chimney-configurator__summary-total"><small>Gesamtpreis</small><strong>${this.money(resolved.total)}</strong><p>inkl. Basissatz und aller gewählten Zusatzteile</p></div>
              <details class="chimney-configurator__parts"${this.partsOpen ? ' open' : ''}>
                <summary>Alle enthaltenen Bauteile anzeigen</summary>
                <div class="chimney-configurator__parts-title h4">Berechnete und gewählte Zusatzteile</div>
                <div data-chargeable-lines>${resolved.chargeable.length ? resolved.chargeable.map((line) => this.summaryLine(line)).join('') : '<p>Keine kostenpflichtigen Zusatzteile.</p>'}</div>
                <div class="chimney-configurator__parts-title h4">Im Basissatz enthalten</div>
                <ul class="chimney-configurator__included">${resolved.included.map(({ componentKey, quantity }) => {
                  const replacement = resolved.replacements.find(({ from }) => from === componentKey);
                  if (replacement) return `<li class="is-replaced"><del>${quantity} × ${this.displayName(componentKey, this.component(componentKey)?.productTitle)}</del><span>Ersetzt durch <strong>${this.displayName(replacement.to, this.component(replacement.to)?.productTitle)}</strong> · Standardteil wird nicht mitgeliefert</span></li>`;
                  return `<li>${quantity} × ${this.displayName(componentKey, this.component(componentKey)?.productTitle)}</li>`;
                }).join('')}</ul>
                <p>Ihr Basissatz enthält bereits die Komponenten für 3 m Schornsteinhöhe. Für Ihre gewählte Gesamthöhe ergänzt der Konfigurator automatisch die zusätzlich benötigten Bauteile.</p>
              </details>
              ${resolved.errors.length ? `<div class="chimney-configurator__error" role="alert">${resolved.errors.join('<br>')}</div>` : ''}
              <button class="button button--full-width" type="button" data-add-configuration${resolved.errors.length || this.submitting ? ' disabled' : ''}>${this.submitting ? 'Wird hinzugefügt…' : 'Komplette Konfiguration in den Warenkorb'}</button>
              <p class="chimney-configurator__cart-note">Alle Bestandteile werden gemeinsam als eine Konfiguration in den Warenkorb gelegt.</p>
              <div data-cart-status role="status" aria-live="polite"></div>
            </section>
            <div class="chimney-configurator__navigation">
              ${this.step > 0 ? '<button class="button button--secondary" type="button" data-back>Zurück</button>' : ''}
              ${this.step < 5 ? `<button class="button" type="button" data-next>${this.returnToSummary ? 'Zur Zusammenfassung' : 'Weiter'}</button>` : ''}
            </div>
          </form>
        </div>`;
    }

    recipeSummary(resolved) {
      const additions = resolved.recipe?.additions || [];
      const parts = additions.map(({ componentKey, quantity }) => `${quantity}× ${this.component(componentKey)?.productTitle || componentKey}`);
      if (resolved.recipe?.additionalWallholders) parts.push(`${resolved.recipe.additionalWallholders}× zusätzlicher Wandhalter`);
      return parts.length ? `<strong>Automatisch zusätzlich:</strong> ${parts.join(', ')}` : '<strong>3-m-Basissatz:</strong> keine zusätzlichen Höhenbauteile erforderlich.';
    }

    inlineQuantity(key, label, value, min, source) {
      const componentKey = key.includes('.') ? this.state.extraHolder.componentKey : key;
      return `<div class="chimney-configurator__inline-quantity"><span>${label}${componentKey && this.isUnavailable(componentKey) ? ' – nicht verfügbar' : ''}</span><span class="chimney-configurator__quantity" role="group" aria-label="Menge ${label}"><button type="button" data-quantity-action="minus" data-quantity-target="${key}" aria-label="Menge verringern"${integer(value) <= integer(min) ? ' disabled' : ''}>−</button><input type="number" inputmode="numeric" data-quantity="${key}" data-source="${source}" min="${min}" value="${value}" aria-label="${label}"><button type="button" data-quantity-action="plus" data-quantity-target="${key}" aria-label="Menge erhöhen">+</button></span></div>`;
    }

    summaryLine(line) {
      const title = this.displayName(line.componentKey, line.variant?.productTitle);
      const technicalTitle = line.variant?.productTitle && line.variant.productTitle !== title ? `<small class="chimney-configurator__technical-name">${line.variant.productTitle}</small>` : '';
      const automaticMinimum = integer(line.sourceQuantities?.height) + integer(line.sourceQuantities?.['height-wallholders']);
      const additionalSelected = Math.max(0, line.quantity - automaticMinimum);
      const mode = line.mode === 'replace'
        ? '<span class="chimney-configurator__line-classification">Ersetzt das im Basissatz enthaltene Standardteil</span>'
        : automaticMinimum
          ? `<span class="chimney-configurator__line-status"><small><strong>Automatisch benötigt:</strong> ${automaticMinimum} Stück</small><small><strong>Zusätzlich gewählt:</strong> ${additionalSelected} Stück</small><small><strong>Gesamtmenge:</strong> ${line.quantity} Stück</small></span>`
          : `<span class="chimney-configurator__line-classification">Zusätzlich gewählt: ${line.quantity} Stück</span>`;
      const unitPrice = line.variant?.price || 0;
      return `<div class="chimney-configurator__summary-line"><div><strong>${title}</strong>${technicalTitle}${mode}</div>${line.editable ? `<span class="chimney-configurator__summary-quantity" role="group" aria-label="Menge ${title}"><button type="button" data-summary-action="minus" data-component="${line.componentKey}" aria-label="Menge verringern"${line.quantity <= line.minimum ? ' disabled' : ''}>−</button><input type="number" inputmode="numeric" value="${line.quantity}" min="${line.minimum}" data-summary-quantity="${line.componentKey}" aria-label="Menge ${title}"><button type="button" data-summary-action="plus" data-component="${line.componentKey}" aria-label="Menge erhöhen">+</button></span>` : ''}<strong class="chimney-configurator__line-price">${line.quantity} × ${this.money(unitPrice)} = ${this.money(unitPrice * line.quantity)}</strong>${automaticMinimum ? `<p class="chimney-configurator__minimum-note">Für Ihre gewählte Höhe von ${String(this.state.height).replace('.', ',')} m werden mindestens ${automaticMinimum} dieser Elemente benötigt.</p>` : ''}</div>`;
    }

    setPath(path, value) {
      if (['length360', 'length540', 'length1080'].includes(path)) {
        this.state.extraLengths = { ...this.state.extraLengths, [path]: value };
        return;
      }
      const [head, tail] = path.split('.');
      if (tail) this.state[head] = { ...this.state[head], [tail]: value };
      else this.state[head] = value;
    }

    onChange(event) {
      const stateKey = event.target.dataset.state;
      if (stateKey) {
        let value = event.target.value || null;
        if (['height'].includes(stateKey)) value = Number(value);
        if (['cleaning', 'fireConnections'].includes(stateKey)) value = integer(value, 1);
        this.setPath(stateKey, value);
        this.state.quantityOverrides = {};
        this.render();
        return;
      }
      const quantity = event.target.dataset.quantity;
      if (quantity) {
        this.setPath(quantity, Math.max(integer(event.target.min), integer(event.target.value)));
        this.render();
        return;
      }
      const component = event.target.dataset.summaryQuantity;
      if (component) {
        const line = this.resolved.chargeable.find((item) => item.componentKey === component);
        this.state.quantityOverrides[component] = Math.max(line.minimum, integer(event.target.value, line.minimum));
        this.render();
      }
    }

    onInput(event) {
      if (event.target.matches('[type="number"]') && integer(event.target.value, -1) >= integer(event.target.min)) this.onChange(event);
    }

    async onClick(event) {
      const info = event.target.closest('[data-info-toggle]');
      if (info) {
        const expanded = info.getAttribute('aria-expanded') === 'true';
        this.closeInfoTooltips();
        if (!expanded) {
          const positionedTooltip = this.positionInfoTooltip(info);
          info.setAttribute('aria-expanded', 'true');
          if (positionedTooltip) {
            positionedTooltip.style.visibility = 'visible';
            positionedTooltip.style.opacity = '1';
          }
        }
        return;
      }
      if (event.target.closest('[data-next]')) {
        this.step = this.returnToSummary ? 5 : Math.min(5, this.step + 1);
        this.returnToSummary = false;
        this.render(); this.focusStep(); return;
      }
      if (event.target.closest('[data-back]')) { this.step = Math.max(0, this.step - 1); this.render(); this.focusStep(); return; }
      const editStep = event.target.closest('[data-edit-step]');
      if (editStep) {
        this.step = Math.max(0, Math.min(4, integer(editStep.dataset.editStep)));
        this.returnToSummary = true;
        this.render(); this.focusStep(); return;
      }
      const quantityButton = event.target.closest('[data-quantity-action]');
      if (quantityButton) {
        const path = quantityButton.dataset.quantityTarget;
        const input = this.querySelector(`[data-quantity="${path}"]`);
        const next = Math.max(integer(input.min), integer(input.value) + (quantityButton.dataset.quantityAction === 'minus' ? -1 : 1));
        this.setPath(path, next); this.render(); return;
      }
      const summaryButton = event.target.closest('[data-summary-action]');
      if (summaryButton) {
        const line = this.resolved.chargeable.find(({ componentKey }) => componentKey === summaryButton.dataset.component);
        const delta = summaryButton.dataset.summaryAction === 'minus' ? -1 : 1;
        this.state.quantityOverrides[line.componentKey] = Math.max(line.minimum, line.quantity + delta);
        this.render(); return;
      }
      if (event.target.closest('[data-add-configuration]')) await this.addToCart();
    }

    focusStep() {
      requestAnimationFrame(() => this.querySelector('.chimney-configurator__step:not([hidden]) .chimney-configurator__step-title')?.focus?.());
    }

    async addToCart() {
      if (this.submitting) return;
      const status = this.querySelector('[data-cart-status]');
      this.submitting = true;
      this.render();
      try {
        const payload = buildCartPayload(this.config, this.resolved);
        await addConfigurationToCart(fetch, window.routes?.cart_add_url || '/cart/add.js', payload);
        const currentStatus = this.querySelector('[data-cart-status]');
        if (currentStatus) currentStatus.textContent = 'Die vollständige Konfiguration wurde hinzugefügt.';
        window.location.assign(window.routes?.cart_url || '/cart');
      } catch (error) {
        this.submitting = false;
        this.render();
        const currentStatus = this.querySelector('[data-cart-status]');
        if (currentStatus) { currentStatus.className = 'chimney-configurator__error'; currentStatus.textContent = error.message || DEFAULT_ERROR; }
      }
    }
  }

  const api = { addConfigurationToCart, buildCartPayload, defaultState, resolveConfiguration, validateCartResponse };
  globalThis.ChimneyConfiguratorEngine = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (globalThis.customElements && !customElements.get('chimney-configurator')) customElements.define('chimney-configurator', ChimneyConfigurator);
})();
